import React, { useCallback } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useReactFlow, useStoreApi } from "@xyflow/react";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { FitIcon, RevertIcon, ZoomInIcon, ZoomOutIcon } from "@icons";
import { getOwnershipFitViewport } from "./ownershipFitView.helpers";
import styles from "./OwnershipCanvasZoomControls.module.scss";

interface OwnershipCanvasZoomControlsProps {
    isLayoutFrozen: boolean;
    onResetLayout: () => void;
}

export const OwnershipCanvasZoomControls: React.FC<
    OwnershipCanvasZoomControlsProps
> = ({ isLayoutFrozen, onResetLayout }) => {
    const store = useStoreApi();
    const { zoomIn, zoomOut, getNodes, getNodesBounds, setViewport } =
        useReactFlow();

    const handleFitView = useCallback(async () => {
        const nodes = getNodes();
        if (nodes.length === 0) {
            return;
        }

        const bounds = getNodesBounds(nodes);
        const { width, height, minZoom } = store.getState();
        if (width === 0 || height === 0) {
            return;
        }

        await setViewport(
            getOwnershipFitViewport({ bounds, width, height, minZoom }),
        );
    }, [getNodes, getNodesBounds, setViewport, store]);

    // react-hotkeys-hook matches event.code (Equal/Minus), not the glyph.
    useKeyboardCommand({
        keys: ["equal", "shift+equal", "add"],
        callback: () => {
            zoomIn();
        },
        options: {
            description: "Zoom in on ownership canvas",
            preventDefault: true,
        },
    });

    useKeyboardCommand({
        keys: ["minus", "subtract"],
        callback: () => {
            zoomOut();
        },
        options: {
            description: "Zoom out on ownership canvas",
            preventDefault: true,
        },
    });

    return (
        <div className={styles.zoomControls}>
            <div className={styles.zoomButtonGroup}>
                <Tooltip content="Zoom out" shortcut="-">
                    <Button
                        className={styles.zoomOutButton}
                        variant="primary"
                        size="md"
                        iconOnly
                        onClick={() => zoomOut()}
                        aria-label="Zoom out"
                    >
                        <ZoomOutIcon width={14} height={14} />
                    </Button>
                </Tooltip>
                <Tooltip content="Zoom in" shortcut="+">
                    <Button
                        className={styles.zoomInButton}
                        variant="primary"
                        size="md"
                        iconOnly
                        onClick={() => zoomIn()}
                        aria-label="Zoom in"
                    >
                        <ZoomInIcon width={14} height={14} />
                    </Button>
                </Tooltip>
            </div>
            <WithHotkey
                hotKey="mod+0"
                tooltip="Fit view"
                hotKeyOptions={{
                    description: "Fit ownership canvas to view",
                    preventDefault: true,
                }}
            >
                <Button
                    variant="primary"
                    size="md"
                    iconOnly
                    onClick={() => void handleFitView()}
                    aria-label="Fit view"
                >
                    <FitIcon width={14} height={14} />
                </Button>
            </WithHotkey>
            {isLayoutFrozen && (
                <Tooltip content="Restore automatic layout">
                    <Button
                        variant="primary"
                        size="md"
                        iconOnly
                        onClick={onResetLayout}
                        aria-label="Restore automatic layout"
                    >
                        <RevertIcon width={14} height={14} />
                    </Button>
                </Tooltip>
            )}
        </div>
    );
};
